import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  message,
  Badge,
  Space,
  Select,
  Table
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newBlog, setNewBlog] = useState({
    id: null,
    title: "",
    content: "",
    status: "draft", // Fixed: Changed to lowercase to match backend validation
    image: null,
    remove_image: false,
  });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(false);

  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ header: 1 }, { header: 2 }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://agpipefitting.org.et/api/blogs");
      setBlogs(response.data);
      setFilteredBlogs(response.data);
    } catch (error) {
      message.error("Error fetching blogs");
    } finally {
      setLoading(false);
    }
  };

  const handleEditBlog = (blog) => {
    setNewBlog({
      id: blog.id,
      title: blog.title,
      content: blog.content,
      status: blog.status || "draft",
      image: blog.image,
      remove_image: false,
    });
    // Fixed: Check if image exists and format correctly
    if (blog.image) {
      const imageUrl = blog.image.includes('http') 
        ? blog.image 
        : `https://agpipefitting.org.et/storage/${blog.image}`;
      setPreview(imageUrl);
    } else {
      setPreview(null);
    }
    setEditing(true);
    setShowModal(true);
  };

  const handleDeleteBlog = async (blogId) => {
    if (window.confirm("Are you sure you want to delete this blog post?")) {
      try {
        await axios.delete(`https://agpipefitting.org.et/api/blogs/${blogId}`);
        setBlogs((prev) => prev.filter((blog) => blog.id !== blogId));
        setFilteredBlogs((prev) => prev.filter((blog) => blog.id !== blogId));
        message.success("Blog deleted successfully");
      } catch (error) {
        message.error("Error deleting blog");
      }
    }
  };

  const handleAddBlog = async () => {
    // Basic validation for title and content (stripped HTML)
    const strippedContent = newBlog.content.replace(/<[^>]*>/g, '').trim();
    if (!newBlog.title.trim() || !strippedContent) {
      message.error("Title and Content are required");
      return;
    }

    const formData = new FormData();
    formData.append("title", newBlog.title);
    formData.append("content", newBlog.content);
    formData.append("status", newBlog.status);

    // Fixed: Handle image removal
    if (newBlog.remove_image) {
      formData.append("remove_image", "1");
    } else if (newBlog.image instanceof File) {
      formData.append("image", newBlog.image);
    } else if (editing && !newBlog.remove_image && typeof newBlog.image === 'string') {
      // Keep existing image if editing and not removing
      formData.append("image", newBlog.image);
    }

    try {
      const config = { 
        headers: { 
          "Content-Type": "multipart/form-data",
          "Accept": "application/json"
        } 
      };
      let response;

      if (editing && newBlog.id) {
        // Use POST with _method=PUT for Laravel compatibility, or PATCH
        formData.append("_method", "PUT");
        response = await axios.post(
          `https://agpipefitting.org.et/api/blogs/${newBlog.id}`,
          formData,
          config
        );
        setBlogs((prev) => prev.map((blog) => 
          blog.id === newBlog.id ? response.data : blog
        ));
        message.success("Blog updated successfully");
      } else {
        // Create new blog
        response = await axios.post(
          "https://agpipefitting.org.et/api/blogs", 
          formData, 
          config
        );
        setBlogs((prev) => [...prev, response.data]);
        message.success("Blog created successfully");
      }

      // Refresh the list
      fetchBlogs();
      handleCloseModal();
    } catch (error) {
      console.error("Error response:", error.response?.data);
      message.error(
        error.response?.data?.message || 
        error.response?.data?.errors?.title?.[0] || 
        error.response?.data?.errors?.content?.[0] || 
        "Error saving blog post"
      );
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(false);
    setNewBlog({ 
      id: null, 
      title: "", 
      content: "", 
      status: "draft", 
      image: null,
      remove_image: false 
    });
    setPreview(null);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredBlogs(blogs);
    } else {
      setFilteredBlogs(
        blogs.filter((blog) => 
          blog.title.toLowerCase().includes(query.toLowerCase()) ||
          blog.content.toLowerCase().includes(query.toLowerCase())
        )
      );
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileType = file.type;
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
      const maxSize = 2 * 1024 * 1024; // 2MB

      if (!validTypes.includes(fileType)) {
        message.error("Invalid image format. Please upload a JPEG, PNG, JPG, or GIF image.");
        return;
      }

      if (file.size > maxSize) {
        message.error("Image size must be less than 2MB");
        return;
      }

      setNewBlog({ 
        ...newBlog, 
        image: file,
        remove_image: false 
      });
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setNewBlog({ ...newBlog, image: null, remove_image: true });
    setPreview(null);
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Blog Management</h1>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={() => {
            setEditing(false);
            setShowModal(true);
          }}
        >
          Add New Post
        </Button>
      </div>

      <Input.Search
        placeholder="Search posts..."
        enterButton={<SearchOutlined />}
        onChange={(e) => handleSearch(e.target.value)}
        value={searchQuery}
        style={{ marginBottom: 16, width: "50%" }}
      />

      <Table
        dataSource={filteredBlogs}
        rowKey="id"
        loading={loading}
        columns={[
          { 
            title: "Title", 
            dataIndex: "title", 
            key: "title",
            ellipsis: true 
          },
          {
            title: "Content",
            dataIndex: "content",
            key: "content",
            render: (content) => (
              <div 
                style={{ 
                  maxHeight: "100px", 
                  overflow: "hidden",
                  textOverflow: "ellipsis" 
                }}
                dangerouslySetInnerHTML={{ 
                  __html: content.length > 100 
                    ? content.substring(0, 100) + "..." 
                    : content 
                }} 
              />
            ),
          },
          {
            title: "Image",
            dataIndex: "image",
            key: "image",
            render: (image) => 
              image ? (
                <img 
                  src={image.includes('http') ? image : `https://agpipefitting.org.et/storage/${image}`} 
                  alt="blog" 
                  width="100" 
                  style={{ objectFit: "cover" }}
                />
              ) : (
                "No Image"
              ),
          },
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
              <Badge 
                status={status === "published" ? "success" : "default"} 
                text={status?.charAt(0).toUpperCase() + status?.slice(1)} 
              />
            ),
            filters: [
              { text: 'Published', value: 'published' },
              { text: 'Draft', value: 'draft' },
            ],
            onFilter: (value, record) => record.status === value,
          },
          {
            title: "Actions",
            key: "actions",
            render: (_, blog) => (
              <Space>
                <Button 
                  icon={<EditOutlined />} 
                  onClick={() => handleEditBlog(blog)}
                >
                  Edit
                </Button>
                <Button 
                  danger 
                  icon={<DeleteOutlined />} 
                  onClick={() => handleDeleteBlog(blog.id)}
                >
                  Delete
                </Button>
              </Space>
            ),
          },
        ]}
      />

      <Modal 
        title={editing ? "Edit Blog" : "New Blog"} 
        open={showModal} 
        onCancel={handleCloseModal}
        onOk={handleAddBlog}
        okText={editing ? "Update" : "Create"}
        cancelText="Cancel"
        width={800}
      >
        <Form layout="vertical">
          <Form.Item 
            label="Title" 
            required
            validateStatus={!newBlog.title.trim() ? "error" : ""}
            help={!newBlog.title.trim() ? "Title is required" : ""}
          >
            <Input 
              value={newBlog.title} 
              onChange={(e) => setNewBlog({ ...newBlog, title: e.target.value })} 
              placeholder="Enter blog title"
            />
          </Form.Item>
          <Form.Item 
            label="Content" 
            required
            validateStatus={!newBlog.content.replace(/<[^>]*>/g, '').trim() ? "error" : ""}
            help={!newBlog.content.replace(/<[^>]*>/g, '').trim() ? "Content is required" : ""}
          >
            <ReactQuill 
              value={newBlog.content} 
              onChange={(content) => setNewBlog({ ...newBlog, content })} 
              modules={quillModules}
              style={{ height: "200px", marginBottom: "50px" }}
            />
          </Form.Item>
          <Form.Item label="Status">
            <Select 
              value={newBlog.status} 
              onChange={(status) => setNewBlog({ ...newBlog, status })}
            >
              <Select.Option value="draft">Draft</Select.Option>
              <Select.Option value="published">Published</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="Image">
            <input 
              type="file" 
              accept="image/*" 
              onChange={handleImageChange}
              style={{ marginBottom: "10px" }}
            />
            {preview && (
              <div>
                <img src={preview} alt="Preview" width="100" className="mt-2" />
                <Button 
                  type="link" 
                  danger 
                  onClick={handleRemoveImage}
                  style={{ paddingLeft: 0 }}
                >
                  Remove Image
                </Button>
              </div>
            )}
            <div style={{ color: "#999", fontSize: "12px" }}>
              Supported formats: JPEG, PNG, JPG, GIF. Max size: 2MB
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BlogPage;